import type { useI18n } from '@bravemove/i18n/react';
import { ApiError } from './types';

type Translator = ReturnType<typeof useI18n>['t'];

/**
 * Map an `ApiError` (or any unknown thrown value) to a translated, user-facing
 * message. Codes not listed fall through to the server-provided message or
 * a generic "something went wrong" string.
 */
export function translateApiError(err: unknown, t: Translator): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'INVALID_CREDENTIALS':
        return t('admin:errors.invalidCredentials');
      case 'ACCOUNT_LOCKED':
        return t('admin:errors.accountLocked');
      case 'ACCOUNT_DISABLED':
        return t('admin:errors.accountDisabled');
      case 'ACCOUNT_SUSPENDED':
        return t('admin:errors.accountSuspended');
      case 'LAST_SUPER_ADMIN':
        return t('admin:errors.lastSuperAdmin');
      case 'INVALID_TOTP':
        return t('admin:errors.invalidTotp');
      case 'INVITE_EXPIRED':
      case 'INVITE_CONSUMED':
        return t('admin:errors.inviteExpired');
      case 'RESET_TOKEN_EXPIRED':
        return t('admin:errors.resetTokenExpired');
      case 'WEAK_PASSWORD':
        return t('admin:errors.weakPassword');
      case 'EMAIL_ALREADY_EXISTS':
        return t('admin:errors.emailExists');
      case 'PERMISSION_DENIED':
        return t('admin:errors.permissionDenied');
      case 'RATE_LIMIT_EXCEEDED':
        return t('admin:errors.rateLimitExceeded');
      case 'RATE_OUT_OF_BOUNDS':
        return t('admin:errors.rateOutOfBounds');
      case 'LARGE_DELTA_REJECTED':
        return t('admin:errors.largeDelta');
      case 'INVALID_SECURITY_KEY':
        return t('admin:securityKeys.errors.invalid');
      case 'SECURITY_KEY_CHALLENGE_EXPIRED':
        return t('admin:securityKeys.errors.expired');
      case 'NO_SECURITY_KEYS':
        return t('admin:securityKeys.errors.noKeys');
      case 'SECURITY_KEY_ALREADY_REGISTERED':
        return t('admin:securityKeys.errors.alreadyAdded');
      case 'SECURITY_KEY_LIMIT_REACHED':
        return t('admin:securityKeys.errors.limitReached');
      case 'SECURITY_KEY_NOT_FOUND':
        return t('admin:securityKeys.errors.notFound');
      case 'VENUE_HAS_FINANCIAL_HISTORY':
        return t('admin:errors.venueHasFinancialHistory');
      default:
        return err.body?.error?.message ?? t('admin:errors.generic');
    }
  }
  if (err instanceof Error && err.message) return t('admin:errors.network');
  return t('admin:errors.generic');
}
